import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Observable } from 'rxjs/internal/Observable';

// Web-conference location types (Zoom, Google Meet, etc). Video meetings are
// not ready yet, so VIDEO_LOCATIONS_ENABLED keeps them out of every location
// picker (event form + internal booking). Flip to true to bring them back.
export const VIDEO_LOCATION_TYPES: string[] = ['zoom', 'google-meet', 'ms-teams', 'webex', 'goto-meeting'];
export const VIDEO_LOCATIONS_ENABLED = false;

export function isVideoLocationType(type: string): boolean {
  return VIDEO_LOCATION_TYPES.includes(type);
}

// True when a location type may be offered in a picker right now.
export function isLocationTypeAvailable(type: string): boolean {
  return VIDEO_LOCATIONS_ENABLED || !isVideoLocationType(type);
}


// Location types to strip from bookable event lists while video meetings are
// off. Empty when the flag is on. Callers pass the return value to backend
// list-fetching methods that support excludeLocationTypes.
export function getExcludedLocationTypesForBooking(): string[] {
  return VIDEO_LOCATIONS_ENABLED ? [] : [...VIDEO_LOCATION_TYPES];
}

@Injectable({ providedIn: 'root' })
export class YeehroCalendlyEventService {
  private API_URL = '/api/yeehro-calendly-events/';
  private headerOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

  constructor(private httpClient: HttpClient) {}


  getAll(): Observable<any[]> {
    return this.httpClient.get<any[]>(this.API_URL);
  }


  getById(id: string): Observable<any> {
    return this.httpClient.get<any>(this.API_URL + id);
  }


  // Public — unauthenticated. Returns { event, userSettings } only when the
  // event is active + not deleted. 404 otherwise. Used by the public booking
  // reservation page when we already know the event id (from an invite tracker
  // or from an ?eventId= query param on the URL).
  getPublicById(id: string): Observable<any> {
    return this.httpClient.get<any>(this.API_URL + 'public/' + id);
  }


  create(event: any): Observable<any> {
    return this.httpClient.post<any>(this.API_URL, JSON.stringify(event), this.headerOptions);
  }


  update(event: any): Observable<any> {
    return this.httpClient.put<any>(this.API_URL + event._id, JSON.stringify(event), this.headerOptions);
  }


  // options.activate=true makes the copy active immediately (used when adopting
  // a template to book with it right away). Defaults to an inactive copy.
  duplicate(id: string, options: { activate?: boolean } = {}): Observable<any> {
    return this.httpClient.post<any>(this.API_URL + 'duplicate/' + id, JSON.stringify(options), this.headerOptions);
  }


  toggleActive(id: string): Observable<any> {
    return this.httpClient.put<any>(this.API_URL + 'toggle-active/' + id, {}, this.headerOptions);
  }


  delete(id: string): Observable<any> {
    return this.httpClient.delete<any>(this.API_URL + id);
  }


  updatePassedFields(id: string, fields: any): Observable<any> {
    return this.httpClient.patch<any>(this.API_URL + id + '/update-passed-fields', JSON.stringify({ fields }), this.headerOptions);
  }


  bulkUpdate(ids: string[], fields: any): Observable<any> {
    return this.httpClient.post<any>(this.API_URL + 'bulk-update', JSON.stringify({ ids, fields }), this.headerOptions);
  }


  search(searchTerms: any): Observable<any[]> {
    return this.httpClient.post<any[]>(this.API_URL + 'search', JSON.stringify(searchTerms), this.headerOptions);
  }


  getCustomDatasource(searchParams: any, sortField = 'createdAt', sortDirection = 'desc', pageNumber: number = 0, pageSize: number = 25): Observable<any> {
    searchParams.sortField = sortField;
    searchParams.sortOrder = sortDirection;
    searchParams.pageNumber = pageNumber;
    searchParams.pageSize = pageSize;
    return this.httpClient.post<any>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchParams), this.headerOptions);
  }


  // Cursor-paginated search — used by the scheduling page list and any other
  // place that paginates events. Mirrors booking service's getCursorPage shape.
  getCursorPage(searchTerms: any, sortField: string = 'createdAt', sortOrder: string = 'desc', cursor: any = null, pageSize: number = 25): Observable<any> {
    const body = { ...(searchTerms || {}), sortField: sortField, sortOrder: sortOrder, cursor: cursor, pageSize: pageSize };
    return this.httpClient.post<any>(this.API_URL + 'cursor-search', JSON.stringify(body), this.headerOptions);
  }


  // Thin-projection list for the availability assignment dropdown — returns
  // only _id name duration color schedule. Lighter than getAll() so the page
  // loads faster as event counts grow.
  getAllForAssignment(): Observable<any[]> {
    return this.httpClient.get<any[]>(this.API_URL + 'list-for-assignment');
  }


  // Slug availability check (read-only). Returns { available, suggested? }.
  // Used by the panel's Verify button.
  checkSlug(eventId: string, slug: string): Observable<any> {
    return this.httpClient.post<any>(this.API_URL + eventId + '/check-slug', JSON.stringify({ slug: slug }), this.headerOptions);
  }


  // Strict slug update — saves only if the requested slug is exactly available.
  // Returns { success, slug?, suggested? }. Server re-checks at write time so
  // the Verify→Save race window is closed.
  updateSlug(eventId: string, slug: string): Observable<any> {
    return this.httpClient.post<any>(this.API_URL + eventId + '/update-slug', JSON.stringify({ slug: slug }), this.headerOptions);
  }


  // Base slug from a human name — lowercase alphanumeric, dash-separated, no
  // leading/trailing dashes. Matches the panel's generateSlug base rule.
  // Uniqueness is enforced server-side on every write path; this helper only
  // covers derivation so all callers slugify identically.
  slugifyName(name: string): string {
    const raw = (name || '').toLowerCase();
    return raw.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'new-meeting';
  }


  // Build a new location object with type-and-mode-aware defaults.
  // Called by BOTH the event-form panel's `addLocation` button (when the user
  // picks a type from the menu) AND the fast-create flow (to seed the initial
  // phone location on new events). Single source of truth for what a freshly
  // added location of type X looks like.
  buildDefaultLocation(type: string, schedulingMode: string): any {
    const loc: any = { type };
    if (type === 'phone') {
      // Group events can't use require-invitee (there's one shared phone for
      // the group, not one per attendee) — default to provide-number. Every
      // other mode defaults to require-invitee (invitee submits their number,
      // host calls them — recruiting norm + host's number stays private).
      loc.phoneOption = schedulingMode === 'group' ? 'provide-number' : 'require-invitee';
    }
    if (type === 'custom') loc.displayOption = 'booking-page';
    return loc;
  }


  // Build a fully-defaulted event record for the selected scheduling mode.
  // Used both by the scheduling-page sidebar "Create" flow AND the event-picker
  // fast-create button. The returned object is shape-compatible with the event
  // form panel's input — it can be opened directly in the panel for editing,
  // or saved as-is via create() because every required field is populated.
  buildDefaultEventFromType(options: {
    schedulingMode: string;
    currentUser: any;
    userSchedule?: any;
    nameOverride?: string;
  }): any {
    const mode = options.schedulingMode || 'one-on-one';
    const user = options.currentUser;

    const eventName = options.nameOverride || 'New Meeting';

    const event: any = {
      name: eventName,
      // Slug derived from the name up front so the booking URL reflects what the
      // host typed from the moment the event is created — rather than defaulting
      // to "new-meeting" until they manually edit it in the panel.
      bookingLinkId: this.slugifyName(eventName),
      duration: 30,
      durationOptions: [{ value: 30, isDefault: true }],
      schedulingMode: mode,
      // Seed with a single phone location — the most common recruiting setup.
      // The host can add/remove/edit in the panel the same way as always.
      locations: [this.buildDefaultLocation('phone', mode)],
      color: { primary: '#8247F5', label: 'Purple' },
      // Always include the creator as a host, every mode. For one-on-one / group
      // this is THE host; for round-robin / collective it's the first of many
      // and the host can add more in the panel.
      hosts: user?._id ? [user._id] : [],
      active: true,
      isBookable: true,
      dateRangeType: 'rolling',
      dateRangeRollingDays: 60,
      dateRangeRollingUnit: 'calendar',
      minNotice: 60,
      minNoticeUnit: 'hours',
      maxInvitees: mode === 'group' ? 2 : null,
      showRemainingSpots: false,
      confirmationDeliveryType: 'email',
      // Seed the notification defaults so a fast-created event matches what the
      // form's seedNotificationDefaults produces (every field populated). Keeps
      // quick-create records complete + consistent with form-created/cloned ones.
      notifications: this.buildDefaultNotifications(),
      created_by: user?._id
    };

    // If the user already has an availability schedule, bind it. Otherwise leave
    // null — the panel lets them pick/create one, and the event can't be
    // booked until a schedule is attached.
    if (options.userSchedule?._id) {
      event.schedule = options.userSchedule._id;
    }

    return event;
  }


  // Default notification set for a new event. Mirrors the event form's
  // seedNotificationDefaults so quick-created records carry the same templated
  // defaults. Consolidate with the form's defaults when the notification editor
  // is brought back.
  buildDefaultNotifications(): any {
    return {
      calendarInvitation: {
        title: '{{invitee_full_name}} and {{my_name}}',
        body: '{{event_name}}\n{{event_description}}\n{{location}}\n{{questions_and_answers}}',
        cancellationPolicy: '',
        includeCancelLinks: true
      },
      emailConfirmation: {
        subject: 'Confirmed: {{event_name}} with {{my_name}} on {{event_date}}',
        body: 'Hi {{invitee_full_name}},\nYour {{event_name}} with {{my_name}} at {{event_time}} on {{event_date}} is scheduled.\n{{event_description}}\n{{location}}\n{{questions_and_answers}}',
        replyTo: 'host',
        cancellationPolicy: '',
        includeCancelLinks: true
      },
      emailCancellation: {
        subject: 'Cancelled: {{event_name}} with {{my_name}} on {{event_date}}',
        body: 'Hi {{invitee_full_name}},\nYour {{event_name}} with {{my_name}} at {{event_time}} on {{event_date}} has been cancelled.',
        replyTo: 'host'
      },
      emailReminders: {
        subject: 'Reminder: {{event_name}} with {{my_name}} at {{event_time}} on {{event_date}}',
        body: 'Hi {{invitee_full_name}},\nThis is a friendly reminder that your {{event_name}} with {{my_name}} is at {{event_time}} on {{event_date}}.\n{{location}}\n{{event_description}}\n{{questions_and_answers}}',
        replyTo: 'host',
        reminders: [{ value: 24, unit: 'hours' }],
        active: false,
        includeCancelLinks: true
      },
      textReminders: {
        message: 'Reminder: {{event_name}} with {{my_name}} at {{event_time}} on {{event_date}}',
        reminders: [{ value: 24, unit: 'hours' }],
        active: false
      },
      emailFollowUp: {
        subject: 'Thank you for your time!',
        body: 'Hi {{invitee_full_name}},\nThank you for attending {{event_name}} at {{event_time}} on {{event_date}}.\nPlease respond to this email with any feedback or additional requests.',
        replyTo: 'host',
        timing: { value: 1, unit: 'hours' },
        active: false
      }
    };
  }
}